// src/pages/CourseTime/utils/deriveSlideLayout.js

// Newer program content ships a `slide_type` per slide instead of the
// client-facing `SlideLayout` string SlideRenderer's registry dispatches on
// (only the assessment/closing segments currently carry SlideLayout directly).
// This maps the slide_type values we've seen onto the SlideLayout a component
// already exists for - slides with no entry here keep falling through to
// DefaultFallbackSlide, same as before.
//
// It follows the PW preview's SLIDE_TYPE_LAYOUT_MAP / getEffectiveLayout
// (dq-seal-client DQProV2 slideRegistry.js) so a slide the preview shows is
// shown here too (LA-IW-15Oct, LA-IW-16Oct). case_study / big_idea / why keep
// TemplateGraphicAndText, which already draws them here.
const SLIDE_TYPE_TO_LAYOUT = {
  intro_video: 'VideoTemplate',
  video: 'VideoTemplate',
  poll: 'POLL_QUIZ_ACTIVITY',
  intro_poll: 'POLL_QUIZ_ACTIVITY',
  tied_poll: 'POLL_QUIZ_ACTIVITY',
  fun_poll: 'POLL_QUIZ_ACTIVITY',
  poll_quiz: 'POLL_QUIZ_ACTIVITY',
  big_idea: 'TemplateGraphicAndText',
  case_study: 'TemplateGraphicAndText',
  why: 'TemplateGraphicAndText',
  key_points: 'TemplateList',
  slo_title: 'TemplateHtmlContent',
  content: 'TemplateHtmlContent',
  content_new: 'TemplateHtmlContent',
  content_more: 'TemplateHtmlContent',
  // Teaching slides HtmlContentSlide draws in its content branch.
  framework: 'TemplateHtmlContent',
  stakes: 'TemplateHtmlContent',
  guide: 'TemplateHtmlContent',
  celebration: 'TemplateHtmlContent',
  scenario: 'TemplateHtmlContent',
  bridge: 'TemplateHtmlContent',
  reveal: 'TemplateHtmlContent',
  // Closing slides (HtmlContentSlide renders their slo_recaps / badge).
  summary: 'TemplateHtmlContent',
  badge: 'TemplateHtmlContent',
  recap: 'TemplateRecap',
  t7_recap: 'TemplateRecap',
  closing: 'TemplateReentryMap',
  reentry_map: 'TemplateReentryMap',
  pledge: 'PledgeTemplate',
  decision_point: 'TemplateDecisionPoint',
  worked_example: 'TemplateWorkedExample',
  analysis_framework: 'TemplateAnalysisFramework',
  journaling: 'JOURNALING_SELF_REFLECTION_ACTIVITY',
  reflection: 'JOURNALING_SELF_REFLECTION_ACTIVITY',
  share_work: 'SHARE_WORK_FILE_ACTIVITY',
  design_challenge: 'SHARE_WORK_FILE_ACTIVITY',
  // Same names the partner preview treats as a discussion (getEffectiveLayout).
  discussion: 'DISCUSSION_ACTIVITY',
  group_discussion: 'DISCUSSION_ACTIVITY',
};

// Some Closing slides are saved with no slide_type at all: a journaling slide
// (journaling_title / prompt) or a celebration (celebration_message /
// badge_label). The preview recognises them by those fields; so does this.
const inferSlideType = (slide) => {
  if (slide.slide_type) return slide.slide_type;
  if (slide.journaling_title || slide.prompt) return 'journaling';
  if (slide.celebration_message || slide.badge_label) return 'celebration';
  return '';
};

// Newer content names its id slide_id (B3_S5), not SlideID, and repeats it in
// every session. The player keys activity state, saved answers and discussions
// by SlideID, so give it one, scoped to the session. Without it every activity
// shared one undefined key: after one answer, every poll lost its Submit.
const withSlideId = (slide, sessionNo) => {
  if (slide.SlideID || !slide.slide_id) return slide;
  return { ...slide, SlideID: sessionNo ? `${sessionNo}_${slide.slide_id}` : slide.slide_id };
};

const deriveSlide = (raw, sessionNo) => {
  if (!raw || typeof raw !== 'object') return raw;
  const slide = withSlideId(raw, sessionNo);
  if (slide.SlideLayout) return slide;

  const slideType = inferSlideType(slide);
  const layout = SLIDE_TYPE_TO_LAYOUT[slideType];
  if (!layout) return slide;

  const derived = { ...slide, slide_type: slideType, SlideLayout: layout };

  if (layout === 'POLL_QUIZ_ACTIVITY') {
    derived.SlideTitle = slide.SlideTitle || slide.question_text || slide.poll_title || '';
    // Poll options here are {label, is_correct, feedback_if_chosen} objects;
    // PollActivity expects a flat array of option label strings.
    if (Array.isArray(slide.options) && !Array.isArray(slide.Options)) {
      derived.Options = slide.options.map(opt => (typeof opt === 'string' ? opt : opt?.label || ''));
    }
  }

  if (layout === 'JOURNALING_SELF_REFLECTION_ACTIVITY') {
    derived.SlideTitle = slide.SlideTitle || slide.journaling_title || '';
    derived.SlideContent = slide.SlideContent || slide.prompt || '';
  }

  if (layout === 'DISCUSSION_ACTIVITY') {
    derived.SlideTitle = slide.SlideTitle || slide.discussion_title || '';
    derived.SlideContent = slide.SlideContent || slide.discussion_prompt || '';
  }

  // A design challenge is a Share Work activity; its title and brief are what
  // the activity (and the Activity Responses tab) name it by.
  if (layout === 'SHARE_WORK_FILE_ACTIVITY') {
    derived.SlideTitle = slide.SlideTitle || slide.challenge_title || slide.title || slide.headline || '';
    derived.SlideContent = slide.SlideContent || slide.challenge_brief || '';
  }

  return derived;
};

export const deriveSlideLayouts = (content, sessionNo) => {
  if (!content || typeof content !== 'object') return content;

  const updated = {};
  Object.keys(content).forEach((key) => {
    const slides = content[key];
    updated[key] = Array.isArray(slides) ? slides.map((slide) => deriveSlide(slide, sessionNo)) : slides;
  });
  return updated;
};
